import { SUPPORTED_BREAKPOINT_KEYS } from "~/constants";

import { round } from "./helpers";

import type { CSSProperties } from "react";
import type { NoteColor } from "~/components/noteCard/NoteCard";
import type { TagProps } from "~/components/tag/Tag";
import type { Tag, Class, Module } from "../graphql/types";
import type {
  ResponsiveProp,
  ResponsivePropObject,
} from "~/components/components";

/**
 * Converts a px value to rem.
 */
export function toRem(n: number) {
  return n / 16;
}

/**
 * Converts a prop value to pixels.
 */
export function toPx(value: unknown) {
  if (value == null || value === "") return undefined;
  return `${String(value).replace(/px$/, "")}px`;
}

/**
 * Converts pixels to a prop value.
 */
export function fromPx(value?: string) {
  if (value == null || value === "") return 0;
  return Number(value.trim().replace("px", ""));
}

/**
 * Computes the `clamp` value used for responsive text.
 */
export function getClampStyles({
  min,
  max,
  vMin = 400,
  vMax = 960,
  slopeUnit = "vw",
}: {
  min: number;
  max: number;
  vMin?: number;
  vMax?: number;
  slopeUnit?: string;
}) {
  const minWidth = toRem(vMin);
  const maxWidth = toRem(vMax);
  const slope = (toRem(max) - toRem(min)) / (maxWidth - minWidth);
  const yAxisIntersection = -minWidth * slope + toRem(min);

  const params = [
    `${toRem(min)}rem`,
    `${round(slope * 100, 4)}${slopeUnit} + ${round(yAxisIntersection, 4)}rem`,
    `${toRem(max)}rem`,
  ];

  return `clamp(${params.join(", ")})`;
}

/**
 * Determines the CSS Properties for content artwork based
 * on a number of signals.
 */
export function getArtworkStyles({
  progress = 0,
}: {
  progress?: number;
}): CSSProperties {
  return {
    filter: `opacity(${Math.max(0.2, progress)}) grayscale(${
      progress === 1 ? 0 : Math.max(0.6, 1 - progress)
    })`,
    mixBlendMode: progress < 1 ? "luminosity" : "normal",
  };
}

/**
 * Provides a mapping of UI parts and their colors, given a note color
 */
export function getNoteColorConfig(color: NoteColor) {
  switch (color) {
    case "white":
      return {
        background: "var(--color-white)",
        border: "var(--color-neutral-10)",
        text: "var(--color-black)",
        metadata: "var(--color-neutral-05)",
        metadataActive: "var(--color-neutral-10)",
      };
    default:
      return {
        background: `var(--color-${color}-10)`,
        border: `var(--color-${color}-20)`,
        text: `var(--color-${color}-70)`,
        metadata: "rgba(255, 255, 255, 0.3)",
        metadataActive: "rgba(255, 255, 255, 0.5)",
      };
  }
}

/**
 * Converts tag API data to a prop.
 */
export function tagDataToProp(
  tags?: Class["tags"] | Module["tags"] | Tag[],
): TagProps[] | undefined {
  return tags?.map((t) => ({
    text: t?.content,
  }));
}

/**
 * Filters out undefined props.
 */
export function definedProps(props = {}) {
  return Object.fromEntries(
    Object.entries(props).filter(([k, v]) => v !== undefined),
  );
}

/**
 * Determines if a prop conforms to the responsive prop
 * config shape.
 */
export function isResponsivePropObject<T = any>(prop: ResponsiveProp<T>) {
  if (!prop) return false;
  if (typeof prop !== "object") return false;
  const keys = Object.keys(prop) as typeof SUPPORTED_BREAKPOINT_KEYS;
  return (
    Boolean(keys.length) &&
    keys.every((k) => SUPPORTED_BREAKPOINT_KEYS.includes(k))
  );
}

/**
 * Creates CSS custom properties for responsive props.
 */
export function createResponsivePropCSSProperties<T = any>(
  prop: ResponsiveProp<T>,
  config: { name: string },
): { [name: string]: T } {
  if (!isResponsivePropObject(prop)) {
    return {
      [config.name]: prop as T,
    };
  }

  const propObj = prop as ResponsivePropObject<T>;

  return {
    ...SUPPORTED_BREAKPOINT_KEYS.reduce((obj, bp) => {
      return {
        ...obj,
        [`${config.name}-${bp}`]: propObj[bp],
      };
    }, {}),
  };
}
